import React, { useContext } from 'react';
import styled from 'styled-components';
import { useWindowSize } from 'hooks';
import { AzLogo, DsLogo, AxLogo } from 'assets/svgs';
import { useLocation } from '@reach/router';
import { FirebaseContext } from 'context';

function Links({ user, isSabcsSwitzerland }) {
  return (
    <LinksContainer>
      {user ? (
        isSabcsSwitzerland ? (
          <>
            <a
              href="https://www.daiichi-sankyo.ch/de/disclaimer/"
              target="_blank"
              rel="noreferrer">
              Daiichi Sankyo CH Disclaimer
            </a>
            <a
              href="https://www.daiichi-sankyo.ch/de/datenschutz/"
              target="_blank"
              rel="noreferrer">
              Daiichi Sankyo CH Data Protection
            </a>
            <a
              href="https://www.daiichi-sankyo.ch/de/impressum/"
              target="_blank"
              rel="noreferrer">
              Daiichi Sankyo CH Imprint
            </a>
            <a
              href="https://www.astrazeneca.ch/en/terms-of-use.html"
              target="_blank"
              rel="noreferrer">
              AstraZeneca CH Legal Notice & Terms of Use
            </a>
            <a
              href="https://www.azprivacy.astrazeneca.com/ch/ch-de/privacy-notices/"
              target="_blank"
              rel="noreferrer">
              AstraZeneca CH Privacy Notice
            </a>
            <a
              href="https://cookienotice.astrazeneca.com/"
              target="_blank"
              rel="noreferrer">
              AstraZeneca CH Cookie Policy
            </a>
          </>
        ) : (
          <>
            <a
              href="https://www.daiichi-sankyo.ie/disclaimer/"
              target="_blank"
              rel="noreferrer">
              Daiichi Sankyo IE Disclaimer
            </a>
            <a
              href="https://www.daiichi-sankyo.ie/data-protection/"
              target="_blank"
              rel="noreferrer">
              Daiichi Sankyo IE Data Protection
            </a>
            <a
              href="https://www.daiichi-sankyo.ie/imprint/"
              target="_blank"
              rel="noreferrer">
              Daiichi Sankyo IE Imprint
            </a>
            <a
              href="https://www.astrazeneca.com/legal-notice-and-terms-of-use.html"
              target="_blank"
              rel="noreferrer">
              AstraZeneca IE Legal Notice & Terms of Use
            </a>
            <a
              href="https://www.azprivacy.astrazeneca.com/ie/EN/privacy-notices/"
              target="_blank"
              rel="noreferrer">
              AstraZeneca IE Privacy Notice
            </a>
            <a
              href="https://policy.cookiereports.com/a82aeacd-en-gb.html"
              target="_blank"
              rel="noreferrer">
              AstraZeneca IE Cookie Policy
            </a>
          </>
        )
      ) : (
        <p>
          Full Terms & Conditions, Data Protection and Cookies Policy available
          after login.
        </p>
      )}
    </LinksContainer>
  );
}

function Footer() {
  const { width } = useWindowSize();
  const { user } = useContext(FirebaseContext);
  const location = useLocation();
  const isSabcsSwitzerland = location?.pathname?.includes(
    '/sessions/sabcs-switzerland/'
  );
  return (
    <>
      <Divider />
      <Container>
        <Logos>
          <div>
            <a
              href="https://www.daiichi-sankyo.ie/"
              target="_blank"
              rel="noreferrer">
              <StyledDsLogo />
            </a>
            <Seperator />
            <a
              href="https://www.astrazeneca.com/country-sites/ireland.html"
              target="_blank"
              rel="noreferrer">
              <StyledAzLogo />
            </a>
          </div>
          <div>
            <p>Produced by</p>
            <a href="https://www.agencyx.ie/" target="_blank" rel="noreferrer">
              <StyledAxLogo />
            </a>
            <p>
              This is a non-promotional, Congress Feedback Program platform,
              incorporating a series of discussion topics, organised and funded
              by Daiichi Sankyo and AstraZeneca on behalf of the Daiichi
              Sankyo/AstraZeneca Alliance. The platform on which this Congress
              Feedback Program is housed is designed and developed by Agency X.
            </p>
          </div>
        </Logos>
        {width >= 1200 && (
          <DesktopSmallPrint>
            This is a non-promotional, Congress Feedback Program platform,
            incorporating a series of discussion topics, organised and funded by
            Daiichi Sankyo and AstraZeneca on behalf of the Daiichi
            Sankyo/AstraZeneca Alliance. The platform on which this Congress
            Feedback Program is housed is designed and developed by Agency X.
          </DesktopSmallPrint>
        )}
        <Copywright>
          <div>
            <p>Copyright © 2025 - Designed and Developed by Agency X</p>
            <p>
              Job Code{' '}
              {isSabcsSwitzerland ? 'CH/ADC/11/25/0003' : 'IE/ADC/10/25/0009'}
            </p>
            <p>Date of Preparation: December 2025</p>
          </div>
          <Links user={user} isSabcsSwitzerland={isSabcsSwitzerland} />
        </Copywright>
      </Container>
      {width >= 1200 && (
        <DesktopCopywright>
          <div>
            <div>
              <p>Copyright © 2025 - Designed and Developed by Agency X</p>
              <p>
                Job Code{' '}
                {isSabcsSwitzerland ? 'CH/ADC/11/25/0003' : 'IE/ADC/10/25/0009'}
                &nbsp;&nbsp;·&nbsp;&nbsp;Date of Preparation: December 2025
              </p>
            </div>
            <Links user={user} isSabcsSwitzerland={isSabcsSwitzerland} />
          </div>
        </DesktopCopywright>
      )}
    </>
  );
}

const Container = styled.footer`
  position: relative;

  @media (min-width: 1200px) {
    align-items: start;
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-column-gap: 1.25rem;
    margin: 0 auto;
    max-width: 1385px;
  }
`;

const Divider = styled.div`
  background-color: var(--light-grey);
  height: 0.063rem;
  left: 0.625rem;
  position: relative;
  width: calc(100% - 1.25rem);
  z-index: -1;
`;

const Logos = styled.div`
  padding: 0.875rem 1.25rem;

  svg {
    width: 100%;
    height: auto;
  }

  > div:first-of-type {
    display: flex;
    gap: 20px;
    justify-content: space-between;
    margin-bottom: 1.4rem;
    max-width: 20.938rem;
    height: auto;
    width: 100%;

    a:first-of-type {
      position: relative;
      top: 0.625rem;
    }

    @media (min-width: 768px) {
      max-width: 560px;

      a:first-of-type {
        position: relative;
        top: 1rem;
      }
    }
  }

  > div:last-of-type {
    p {
      font-size: 0.75rem;

      &:last-of-type {
        letter-spacing: -0.12px;
        line-height: 140%;
        margin-bottom: 0.3rem;
        margin-top: 1.05rem;
        max-width: 610px;
      }
    }
  }

  @media (min-width: 1200px) {
    display: flex;
    justify-content: space-between;
    grid-column: 2/-2;
    grid-row: 1/2;
    padding: 2.45rem 0 0.75rem;

    > div:last-of-type {
      p {
        font-size: 1rem;
        margin-top: 0.4em;
      }

      p:last-of-type {
        display: none;
      }
    }
  }
`;

const StyledDsLogo = styled(DsLogo)`
  max-width: 9.563rem;

  @media (min-width: 768px) {
    max-width: 16rem;
  }
`;

const StyledAzLogo = styled(AzLogo)`
  max-width: 7.813rem;

  @media (min-width: 768px) {
    max-width: 13rem;
  }
`;

const StyledAxLogo = styled(AxLogo)`
  margin-top: 0.8rem;
  max-width: 7.563rem;

  @media (min-width: 768px) {
    max-width: 10.813rem;
  }

  @media (min-width: 1200px) {
    margin-top: 1.05rem;
  }
`;

const Seperator = styled.div`
  background: var(--light-grey);
  position: relative;
  top: 0.325rem;
  width: 0.063rem;
`;

const DesktopSmallPrint = styled.p`
  font-size: 14px;
  grid-column: 2/-2;
  grid-row: 2/3;
  letter-spacing: -0.12px;
  line-height: 140%;
  margin-bottom: 1.55rem;
`;

const Copywright = styled.div`
  background: #fff;
  font-size: 0.75rem;
  line-height: 1.429em;
  padding: 0.75rem 1.25rem;

  @media (min-width: 1200px) {
    display: none;
  }
`;

const DesktopCopywright = styled.div`
  background: #fff;
  font-size: 0.75rem;
  line-height: 1.429em;
  padding: 0.75rem 0;

  > div {
    display: grid;
    grid-column-gap: 1.25rem;
    grid-template-columns: repeat(12, 1fr);
    margin: 0 auto;
    max-width: 1385px;

    > div:nth-of-type(1) {
      display: flex;
      grid-column: 2/-2;
      grid-row: 1/2;
      justify-content: flex-start;

      p:nth-of-type(1) {
        margin-right: 3.5rem;
      }
    }
  }
`;

const LinksContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.325rem 1.5rem;
  grid-column: 2/-1;
  grid-row: 2/3;
  margin-top: 1rem;

  a {
    color: var(--brown);
    font-size: 0.75rem;
    text-decoration: underline;
  }

  a:hover,
  a:active,
  a:focus {
    color: var(--brown);
  }
`;

export default Footer;
